import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, LockKeyhole, Maximize2, MonitorPlay, ShieldCheck, UserRound, X } from 'lucide-react';
import {
  clearCourseToken,
  courseLogin,
  courseLogout,
  fetchCourseContent,
  fetchCourseList,
  getCourseToken,
  getCoursewarePlayerUrl,
} from '../course/api';
import './CoursePortal.css';

const T = {
  orgEntry: '\u673a\u6784\u6388\u6743\u5165\u53e3',
  authTitle: '\u8bfe\u7a0b\u5185\u5bb9\u9700\u8981\u6388\u6743\u8bbf\u95ee',
  authDesc: '\u8001\u5e08\u767b\u5f55\u540e\uff0c\u7cfb\u7edf\u4f1a\u6309\u673a\u6784\u6388\u6743\u52a8\u6001\u52a0\u8f7d\u8bfe\u7a0b\uff0c\u5e76\u5728\u6bcf\u6b21\u6253\u5f00\u65f6\u6ce8\u5165\u53ef\u8ffd\u8e2a\u6c34\u5370\u3002',
  orgCode: '\u673a\u6784\u7801',
  phone: '\u8001\u5e08\u624b\u673a\u53f7',
  login: '\u8fdb\u5165\u8bfe\u7a0b\u7cfb\u7edf',
  loggingIn: '\u767b\u5f55\u4e2d',
  systemTitle: '\u9632\u76d7\u7248\u8bfe\u7a0b\u4ea4\u4ed8\u7cfb\u7edf',
  logout: '\u9000\u51fa',
  authorizedCourses: '\u6388\u6743\u8bfe\u7a0b',
  noCourses: '\u5f53\u524d\u673a\u6784\u6682\u65e0\u6388\u6743\u8bfe\u7a0b\u3002',
  selectCourse: '\u9009\u62e9\u8bfe\u7a0b',
  openHint: '\u70b9\u51fb\u5de6\u4fa7\u8bfe\u7a0b\u540e\uff0c\u540e\u7aef\u4f1a\u6821\u9a8c\u6388\u6743\u5e76\u8fd4\u56de\u5e26\u6c34\u5370\u7684\u52a8\u6001\u5185\u5bb9\u3002',
  openCourse: '\u6253\u5f00\u8bfe\u7a0b',
  loading: '\u52a0\u8f7d\u4e2d',
  licenseUntil: '\u6388\u6743\u6709\u6548\u671f\u81f3',
  traceCode: '\u8ffd\u8e2a\u7801',
  accountTrace: '\u6bcf\u4e2a\u8d26\u53f7\u72ec\u7acb\u8ffd\u8e2a',
  expireBlock: '\u6388\u6743\u5230\u671f\u81ea\u52a8\u62e6\u622a',
  noDownload: '\u5185\u5bb9\u4e0d\u63d0\u4f9b\u4e0b\u8f7d\u63a5\u53e3',
  loggedTeacher: '\u5df2\u767b\u5f55\u8001\u5e08',
  authorizedOrg: '\u5df2\u6388\u6743\u673a\u6784',
  traced: '\u5df2\u751f\u6210\u8ffd\u8e2a\u7801\uff1a',
  deviceBound: '\u8bbe\u5907\u5df2\u7ed1\u5b9a\uff1a',
  fullscreen: '\u5168\u5c4f\u64ad\u653e',
  openWindow: '\u65b0\u7a97\u53e3\u6253\u5f00',
  closeFullscreen: '\u9000\u51fa\u5168\u5c4f',
};

function WatermarkLayer({ watermark }) {
  if (!watermark) return null;
  const text = `${watermark.tenant} / ${watermark.user} / ${watermark.traceCode}`;
  return (
    <div className="course-watermark" aria-hidden="true">
      {Array.from({ length: 24 }).map((_, index) => <span key={index}>{text}</span>)}
    </div>
  );
}

function LoginPanel({ onLogin }) {
  const [orgCode, setOrgCode] = useState('demo');
  const [phone, setPhone] = useState('13800000000');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await courseLogin({ orgCode, phone });
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="course-auth-panel">
      <div>
        <p className="course-kicker">{T.orgEntry}</p>
        <h1>{T.authTitle}</h1>
        <p>{T.authDesc}</p>
      </div>
      <form className="course-login-form" onSubmit={submit}>
        <label>{T.orgCode}<input value={orgCode} onChange={(event) => setOrgCode(event.target.value)} /></label>
        <label>{T.phone}<input value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
        {error && <div className="course-error">{error}</div>}
        <button type="submit" disabled={loading}><LockKeyhole size={18} />{loading ? T.loggingIn : T.login}</button>
      </form>
    </section>
  );
}

export default function CoursePortal() {
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursePackages, setCoursePackages] = useState([]);
  const [activeCourseId, setActiveCourseId] = useState('');
  const [coursePayload, setCoursePayload] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fullscreenCourseware, setFullscreenCourseware] = useState(false);

  const activeCourse = useMemo(() => courses.find((course) => course.id === activeCourseId), [courses, activeCourseId]);
  const coursewareSrc = coursePayload?.content?.type === 'html-courseware'
    ? getCoursewarePlayerUrl(coursePayload.course.id)
    : '';

  useEffect(() => {
    if (!getCourseToken()) return;
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadCourses(nextActiveId) {
    setError('');
    setLoading(true);
    try {
      const data = await fetchCourseList();
      setCourses(data.courses || []);
      setCoursePackages(data.packages || []);
      const targetId = nextActiveId || activeCourseId || data.courses?.[0]?.id || '';
      if (targetId) setActiveCourseId(targetId);
      setUser((prev) => prev || { name: T.loggedTeacher, tenantName: T.authorizedOrg });
      return data.courses || [];
    } catch (err) {
      clearCourseToken();
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function openCourse(courseId = activeCourseId) {
    if (!courseId) return;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const data = await fetchCourseContent(courseId);
      setCoursePayload(data);
      setActiveCourseId(courseId);
      setMessage(`${T.traced}${data.watermark.traceCode}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  async function logout() {
    await courseLogout();
    setUser(null);
    setCourses([]);
    setActiveCourseId('');
    setCoursePayload(null);
    setMessage('');
    setError('');
    setFullscreenCourseware(false);
  }

  function openCoursewareWindow() {
    if (coursewareSrc) window.open(coursewareSrc, '_blank', 'noopener,noreferrer');
  }

  return (
    <main className="course-portal page-top">
      <div className="container-x">
        {!user ? (
          <LoginPanel onLogin={(nextUser) => { setUser(nextUser); loadCourses(); }} />
        ) : (
          <>
            <section className="course-toolbar">
              <div><p className="course-kicker">Course SaaS MVP</p><h1>{T.systemTitle}</h1><p>{user.tenantName} / {user.name}</p></div>
              <button className="course-ghost-btn" onClick={logout}>{T.logout}</button>
            </section>


            <section className="course-dashboard">
              <aside className="course-side">
                <div className="course-side-title"><MonitorPlay size={18} />{T.authorizedCourses}</div>
                {coursePackages.map((pkg) => (
                  <div key={pkg.id || '__ungrouped__'} className="course-side-group">
                    <div className="course-side-group-title">{pkg.title}</div>
                    {pkg.courses.map((course) => (
                      <button key={course.id} className={course.id === activeCourseId ? 'active' : ''} onClick={() => openCourse(course.id)}>
                        <strong>{course.title}</strong><span>{course.level} / v{course.version}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {!coursePackages.length && !loading && <p className="course-empty">{T.noCourses}</p>}
              </aside>

              <section className="course-player">
                <WatermarkLayer watermark={coursePayload?.watermark} />
                {!coursePayload ? (
                  <div className="course-player-empty">
                    <ShieldCheck size={44} /><h2>{activeCourse ? activeCourse.title : T.selectCourse}</h2><p>{T.openHint}</p>
                    <button onClick={() => openCourse()} disabled={!activeCourseId || loading}>{loading ? T.loading : T.openCourse}</button>
                  </div>
                ) : coursePayload.content.type === 'html-courseware' ? (
                  <div className="course-slide-stack html-courseware-stack">
                    <div className="course-license-strip">{T.licenseUntil} {coursePayload.license.endDate} / {T.traceCode} {coursePayload.watermark.traceCode}</div>
                    <div className="courseware-head">
                      <h2>{coursePayload.content.title}</h2>
                      <div className="courseware-actions">
                        <button type="button" onClick={() => setFullscreenCourseware(true)}><Maximize2 size={16} />{T.fullscreen}</button>
                        <button type="button" onClick={openCoursewareWindow}><ExternalLink size={16} />{T.openWindow}</button>
                      </div>
                    </div>
                    <iframe title={coursePayload.content.title} className="courseware-frame" src={coursewareSrc} allowFullScreen />
                  </div>
                ) : (
                  <div className="course-slide-stack">
                    <div className="course-license-strip">{T.licenseUntil} {coursePayload.license.endDate} / {T.traceCode} {coursePayload.watermark.traceCode}</div>
                    <h2>{coursePayload.content.title}</h2>
                    <div className="course-slides">
                      {coursePayload.content.slides.map((slide, index) => (
                        <article key={`${slide.title}-${index}`} className="course-slide"><span>{String(index + 1).padStart(2, '0')}</span><h3>{slide.title}</h3><p>{slide.body}</p></article>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </section>
            <section className="course-security-notes"><div><UserRound size={18} /> {T.accountTrace}</div><div><ShieldCheck size={18} /> {T.expireBlock}</div><div><LockKeyhole size={18} /> {T.noDownload}</div></section>
            {fullscreenCourseware && coursePayload?.content?.type === 'html-courseware' && (
              <div className="courseware-fullscreen" role="dialog" aria-modal="true">
                <div className="courseware-fullscreen-bar">
                  <strong>{coursePayload.content.title}</strong>
                  <div>
                    <button type="button" onClick={openCoursewareWindow}><ExternalLink size={16} />{T.openWindow}</button>
                    <button type="button" onClick={() => setFullscreenCourseware(false)}><X size={16} />{T.closeFullscreen}</button>
                  </div>
                </div>
                <iframe title={`${coursePayload.content.title} fullscreen`} src={coursewareSrc} allowFullScreen />
              </div>
            )}
          </>
        )}

        {message && <div className="course-toast">{message}</div>}
        {error && <div className="course-toast error">{error}</div>}
      </div>
    </main>
  );
}




